import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { printDieselConsumption } from '../../utils/dieselConsumptionPrint';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelDieselConsumption,
    deleteDieselConsumption,
    getDieselConsumptionList,
} from '../../services/DieselConsumptionServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';

function DieselConsumptionTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status === true || p.status === 1 || p.status === 'Active')
                .map((p) => ({
                    value: p.id,
                    label: p.plant_alias,
                })));
            setIsInitialized(true);
        });
    }, [cookies]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, []),
        [salesSettings, plants],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;
        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        return getDieselConsumptionList(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.diesel_consumption_list || []).map((row) => {
                        const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                        return {
                            ...row,
                            consumption_no_display: row.consumption_no,
                            status_label: isActive ? 'Active' : 'Cancelled',
                            hide_row_edit: isActive ? 'No' : 'Yes',
                            hide_row_cancel: isActive ? 'No' : 'Yes',
                            hide_row_delete: isActive ? 'Yes' : 'No',
                            hide_row_print: 'No',
                        };
                    }),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load diesel consumption notes.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    useEffect(() => {
        if (isInitialized) {
            fetchRecords();
        }
    }, [fetchRecords, isInitialized]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'consumption_no_display', headerName: 'Consumption No' },
        { fieldName: 'consumption_date', headerName: 'Date' },
        { fieldName: 'equipment_summary', headerName: 'Equipment' },
        { fieldName: 'total_diesel_qty', headerName: 'Diesel Qty', align: 'right' },
        { fieldName: 'issued_by', headerName: 'Issued By' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const exportColumns = useMemo(
        () => columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
        [columns],
    );

    const handleOpen = (id) => {
        const row = data.records.find((r) => String(r.id) === String(id));
        const isActive = row?.status_label === 'Active';
        navigate(isActive ? `/DieselConsumptionEdit/${id}` : `/DieselConsumptionView/${id}`);
    };

    const handleCancel = (id) => {
        Swal.fire({
            title: 'Cancel diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;
            cancelDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Cancelled', 'Diesel consumption cancelled.', 'success');
                    fetchRecords();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'));
        });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Record deleted.', 'success');
                    fetchRecords();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'));
        });
    };

    const handlePrint = (id) => {
        printDieselConsumption(cookies, id).catch((error) => {
            displayError(error?.response?.data || error, 'Print Failed');
        });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            'diesel_consumption_notes',
            exportColumns,
            data.records,
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'Diesel Consumption Notes',
            exportColumns,
            data.records,
        );
    };

    return (
        <>
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    title="Diesel Consumption Notes"
                    formLink="/DieselConsumption"
                    addNewLabel="Main Form"
                    recordCount={data.records.length}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    modeLabel=""
                >
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                        onRowEdit={handleOpen}
                        onRowCancel={handleCancel}
                        onRowDelete={handleDelete}
                        onRowPrint={handlePrint}
                        onCSVExport={handleCSVExport}
                        onPDFExport={handlePDFExport}
                        hidePrint={false}
                        compactRows
                        autoPinActionColumn={false}
                    />
                </ListPageLayout>
            </div>
            <SalesListSettingsModal
                show={showSettings}
                onClose={() => setShowSettings(false)}
                plants={plants}
                settings={salesSettings}
                onSave={(next) => {
                    const saved = saveSalesListSettings(cookies.myUserId, next);
                    setSalesSettings(saved);
                    setShowSettings(false);
                }}
            />
        </>
    );
}

export default DieselConsumptionTable;
